import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private _online: Subject<boolean> = new BehaviorSubject<boolean>(undefined);
  public readonly onNetworkStateChange: Observable<boolean> = this._online.asObservable();

  online: boolean;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformServer(this.platformId)) return;
    this.online = window.navigator.onLine;
    this._online.next(this.online);

    window.addEventListener(
      'online',
      () => {
        this._online.next(true);
        this.online = true;
      },
      { passive: true }
    );
    window.addEventListener(
      'offline',
      () => {
        this._online.next(false);
        this.online = false;
      },
      { passive: true }
    );
  }

  public async onNetworkOnline(): Promise<void> {
    await this.onNetwork();
    if (await this.fileCheck()) return;
    await sleep(1000 * 5);
    return this.onNetworkOnline();
  }

  private onNetwork(): Promise<void> {
    let sub: Subscription;
    return new Promise(resolve => {
      setTimeout(() => {
        if (sub) {
          sub.unsubscribe();
          console.debug('network check timed out');
          sub = undefined;
          resolve();
        }
      }, 1000 * 10);
      sub = this.onNetworkStateChange.subscribe(async e => {
        await sleep(0);
        if (!sub) return;
        if (!e) return;
        sub.unsubscribe();
        sub = undefined;
        resolve();
      });
    });
  }

  private async fileCheck(): Promise<boolean> {
    try {
      await lastValueFrom(this.http.head('/assets/network-test.json?cachebust=' + randomString(12)));
      return true;
    } catch (err) {
      console.warn('Network test failed, trying again in a few seconds');
      return false;
    }
  }
}
